import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Card from "../templates/ui/Card";
import Button from "../templates/ui/Button";
import { useCampaignStore } from "../app/store/campaignStore";
import { muteluApi } from "../services/muteluApi";
import aiFortuneData from "../data/aiFortuneData.json";
import { createMysticReading } from "../utils/mysticEngine";
import { logPageApiCall, logPageApiError, logPageApiResult } from "../utils/apiDebugLogger";
import elementWaterImage from "../../../assets/images/game-mutelu/element-water.svg";
import elementEarthImage from "../../../assets/images/game-mutelu/element-earth.svg";
import elementWindImage from "../../../assets/images/game-mutelu/element-wind.svg";
import elementFireImage from "../../../assets/images/game-mutelu/element-fire.svg";
import "./ReviewResult.css";

const GRAPH_LAYOUT = {
  width: 320,
  height: 188,
  left: 34,
  right: 10,
  top: 14,
  bottom: 34,
};

const GRAPH_PLOT_WIDTH = GRAPH_LAYOUT.width - GRAPH_LAYOUT.left - GRAPH_LAYOUT.right;
const GRAPH_PLOT_HEIGHT = GRAPH_LAYOUT.height - GRAPH_LAYOUT.top - GRAPH_LAYOUT.bottom;

function normalizeElementId(value) {
  const raw = String(value || "")
    .trim()
    .toLowerCase();
  if (!raw) return "water";
  const map = {
    wind: "wind",
    air: "wind",
    water: "water",
    earth: "earth",
    fire: "fire",
    neutral: "neutral",
  };
  return map[raw] || raw;
}

function toGraphCoordinates(points) {
  if (!points?.length) return [];
  const stepX = points.length > 1 ? GRAPH_PLOT_WIDTH / (points.length - 1) : 0;

  return points.map((point, index) => {
    const x = GRAPH_LAYOUT.left + index * stepX;
    const y = GRAPH_LAYOUT.top + ((100 - point) / 100) * GRAPH_PLOT_HEIGHT;
    return { x, y };
  });
}

function buildSmoothPath(coords) {
  if (!coords?.length) return "";
  if (coords.length === 1) return `M ${coords[0].x} ${coords[0].y}`;

  let path = `M ${coords[0].x} ${coords[0].y}`;
  for (let i = 0; i < coords.length - 1; i += 1) {
    const p0 = coords[i - 1] || coords[i];
    const p1 = coords[i];
    const p2 = coords[i + 1];
    const p3 = coords[i + 2] || p2;

    const cp1x = p1.x + (p2.x - p0.x) / 6;
    const cp1y = p1.y + (p2.y - p0.y) / 6;
    const cp2x = p2.x - (p3.x - p1.x) / 6;
    const cp2y = p2.y - (p3.y - p1.y) / 6;

    path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
  }
  return path;
}

function buildAreaPath(
  coords,
  baseline = GRAPH_LAYOUT.height - GRAPH_LAYOUT.bottom
) {
  if (!coords?.length) return "";
  const smoothPath = buildSmoothPath(coords);
  const first = coords[0];
  const last = coords[coords.length - 1];
  return `${smoothPath} L ${last.x} ${baseline} L ${first.x} ${baseline} Z`;
}

function toSafeDate(rawValue) {
  if (!rawValue) return null;
  const date = new Date(rawValue);
  return Number.isNaN(date.getTime()) ? null : date;
}

function buildGraphDateLabels(reading, pointCount) {
  const safeCount = Number(pointCount || 0);
  if (safeCount <= 0) return [];

  const baseDate =
    toSafeDate(reading?.scan?.scanned_at) ||
    toSafeDate(reading?.currentDateTime) ||
    new Date();

  const startOffset = -Math.floor((safeCount - 1) / 2);

  return Array.from({ length: safeCount }, (_, index) => {
    const date = new Date(baseDate);
    date.setDate(baseDate.getDate() + startOffset + index);
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${month}/${day}`;
  });
}

function extractDataPayload(payload) {
  if (!payload) return null;
  if (payload?.data && typeof payload.data === "object") return payload.data;
  if (typeof payload === "object") return payload;
  return null;
}

function normalizeGraphPoints(points) {
  if (!Array.isArray(points) || points.length < 2) return [68, 35, 74, 28, 63];
  const normalized = points
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value))
    .map((value) => Math.max(0, Math.min(100, value)));
  return normalized.length >= 2 ? normalized : [68, 35, 74, 28, 63];
}

function normalizeProductEntry(entry) {
  if (typeof entry === "string") {
    const label = entry.trim();
    return label ? { label, supportText: "" } : null;
  }
  if (!entry || typeof entry !== "object") return null;
  const label = String(entry.label || entry.name || "").trim();
  const supportText = String(entry.supportText || entry.description || "").trim();
  if (!label) return null;
  return { label, supportText };
}

function getProductSupportText(label, elementId) {
  const normalizedElementId = normalizeElementId(elementId);
  const byElement =
    aiFortuneData?.productSupportByElement?.[normalizedElementId]?.[label] ||
    aiFortuneData?.productSupportByElement?.all?.[label];
  if (typeof byElement === "string" && byElement.trim()) return byElement.trim();

  const byMap = aiFortuneData?.productSupportText?.[label];
  if (typeof byMap === "string" && byMap.trim()) return byMap.trim();

  return "ช่วยเสริมพลังธาตุประจำวัน และเพิ่มความคล่องตัวเรื่องโชค";
}

function buildRemedyList(reading) {
  const fromReading = Array.isArray(reading?.remedyList) ? reading.remedyList : [];
  if (fromReading.length > 0) return fromReading;

  const fromElement =
    aiFortuneData?.elements?.[normalizeElementId(reading?.dominantElementId)]
      ?.remedyTips;
  if (Array.isArray(fromElement) && fromElement.length > 0) return fromElement;

  const fromFortuneTone = aiFortuneData?.fortuneTemplates?.[reading?.fortuneTone]?.adviceBullets;
  if (Array.isArray(fromFortuneTone) && fromFortuneTone.length > 0) return fromFortuneTone;

  const fromGlobal = aiFortuneData?.reviewMessages?.defaultRemedyList;
  return Array.isArray(fromGlobal) ? fromGlobal : [];
}

function renderTipLine(tip, index) {
  const text = String(tip || "").trim();
  if (!text) return null;
  const splitIndex = text.indexOf(":");
  if (splitIndex > 0) {
    const lead = text.slice(0, splitIndex).trim();
    const rest = text.slice(splitIndex + 1).trim();
    if (lead && rest) {
      return (
        <li key={`${lead}-${index}`}>
          <strong>{lead}:</strong> {rest}
        </li>
      );
    }
  }
  return <li key={`${text}-${index}`}>{text}</li>;
}

function normalizeApiReading(apiPayload) {
  if (!apiPayload || typeof apiPayload !== "object") return null;
  const normalizedElementId = normalizeElementId(
    apiPayload.dominant_element_id || apiPayload.element || "water",
  );
  const elementConfig = aiFortuneData?.elements?.[normalizedElementId];
  const normalizeFortuneEntry = (entry) => {
    if (!entry) return null;
    if (typeof entry === "string") {
      return {
        headline: entry.trim(),
        message: "",
      };
    }
    if (typeof entry !== "object") return null;
    const headline = String(entry.headline || entry.title || entry.name || "").trim();
    const message = String(entry.message || entry.description || entry.text || "").trim();
    if (!headline && !message) return null;
    return { headline, message };
  };
  const fortuneOptionsRaw =
    (Array.isArray(apiPayload.element_fortunes) && apiPayload.element_fortunes) ||
    (Array.isArray(apiPayload.fortune_options) && apiPayload.fortune_options) ||
    (Array.isArray(apiPayload.fortunes) && apiPayload.fortunes) ||
    [];
  const fortuneOptions = fortuneOptionsRaw.map(normalizeFortuneEntry).filter(Boolean);

  return {
    receiptNo: apiPayload.receipt_no || "",
    dominantElementId: normalizedElementId,
    dominantElementLabel:
      apiPayload.dominant_element_label || elementConfig?.label || "ธาตุน้ำ",
    dominantElementSymbol:
      apiPayload.dominant_element_symbol || elementConfig?.symbol || "💧",
    dominantElementMeaning: elementConfig?.meaning || "",
    elementKeywords: Array.isArray(elementConfig?.keywords)
      ? elementConfig.keywords
      : [],
    fixProducts: Array.isArray(apiPayload.fix_products) ? apiPayload.fix_products : [],
    boostProducts: Array.isArray(apiPayload.boost_products) ? apiPayload.boost_products : [],
    graphPoints: normalizeGraphPoints(apiPayload.graph_points),
    currentDateTime: apiPayload.current_datetime || new Date().toLocaleString("th-TH"),
    fortuneStatus: "ดวงวันนี้",
    fortuneHeadline: apiPayload.fortune_headline || "คำทำนายจากใบเสร็จ",
    fortuneMessage: apiPayload.fortune_message || "",
    fortuneUpgradeText: "",
    remedyHeadline: "คำแนะนำ",
    remedyList: [],
    purchasedItems: [],
    dominantProduct: null,
    fortuneTone: "rise",
    lifeGraph: apiPayload.life_graph || null,
    scan: apiPayload.scan || null,
    fortuneOptions,
  };
}

const ELEMENT_IMAGE_MAP = {
  water: elementWaterImage,
  earth: elementEarthImage,
  wind: elementWindImage,
  air: elementWindImage,
  fire: elementFireImage,
};

export default function ReviewResult() {
  const { state, setFortuneResult } = useCampaignStore();
  const [activeTab, setActiveTab] = useState(0);
  const [latestApiResult, setLatestApiResult] = useState(null);
  const [loadingLatest, setLoadingLatest] = useState(false);
  const latestLoadedRef = useRef(false);
  const [selectedFortune, setSelectedFortune] = useState(null);
  const selectedFortuneKeyRef = useRef("");

  const fallbackReading = useMemo(() => {
    if (!state.receiptNo) return null;
    return createMysticReading(state.receiptNo, aiFortuneData);
  }, [state.receiptNo]);
  const apiReading = useMemo(
    () => normalizeApiReading(state.fortuneResult || latestApiResult),
    [latestApiResult, state.fortuneResult],
  );
  const reading = apiReading || fallbackReading;

  useEffect(() => {
    if (latestLoadedRef.current) return;
    if (state.fortuneResult) {
      latestLoadedRef.current = true;
      return;
    }
    latestLoadedRef.current = true;

    const loadLatest = async () => {
      if (!state.fortuneResult) {
        setLoadingLatest(true);
      }
      try {
        logPageApiCall("ReviewResult", "muteluApi.getLatestReceiptResult");
        const response = await muteluApi.getLatestReceiptResult();
        logPageApiResult("ReviewResult", "muteluApi.getLatestReceiptResult", response);
        const payload = extractDataPayload(response);
        if (!payload) return;
        setLatestApiResult(payload);
        setFortuneResult(payload);
      } catch (error) {
        logPageApiError("ReviewResult", "muteluApi.getLatestReceiptResult", error);
        if (!state.fortuneResult) {
          setLatestApiResult(null);
        }
      } finally {
        setLoadingLatest(false);
      }
    };

    loadLatest();
  }, [setFortuneResult, state.fortuneResult]);

  const fortuneOptions = useMemo(() => {
    if (!reading) return [];
    const fromApi = Array.isArray(reading.fortuneOptions)
      ? reading.fortuneOptions.filter((item) => item?.headline || item?.message)
      : [];
    if (apiReading && fromApi.length > 0) {
      return fromApi;
    }
    if (apiReading && (reading.fortuneHeadline || reading.fortuneMessage)) {
      return [
        {
          headline: reading.fortuneHeadline || "คำทำนาย",
          message: reading.fortuneMessage || "",
        },
      ];
    }

    const normalizedElementId = normalizeElementId(reading.dominantElementId);
    const elementFortunes =
      aiFortuneData?.elements?.[normalizedElementId]?.fortunes;
    if (Array.isArray(elementFortunes) && elementFortunes.length > 0) {
      return elementFortunes.map((item) => ({
        headline: item?.headline || "",
        message: item?.message || "",
      }));
    }
    const fallbackFortunes = aiFortuneData?.elements?.water?.fortunes;
    if (Array.isArray(fallbackFortunes) && fallbackFortunes.length > 0) {
      return fallbackFortunes.map((item) => ({
        headline: item?.headline || "",
        message: item?.message || "",
      }));
    }
    return [];
  }, [apiReading, reading]);

  const computedFortune = useMemo(() => {
    if (!reading || fortuneOptions.length === 0) return null;
    const seedText = [
      reading.receiptNo || "",
      reading.scan?.scanned_at || "",
      reading.scan?.id || "",
      reading.fortuneHeadline || "",
    ].join("|");
    let hash = 0;
    for (let i = 0; i < seedText.length; i += 1) {
      hash = (hash * 31 + seedText.charCodeAt(i)) >>> 0;
    }
    const randomIndex = hash % fortuneOptions.length;
    return fortuneOptions[randomIndex] || fortuneOptions[0] || null;
  }, [fortuneOptions, reading]);

  useEffect(() => {
    const nextKey = [
      reading?.receiptNo || "",
      reading?.scan?.scanned_at || "",
      reading?.scan?.trn_no || "",
      reading?.fortuneHeadline || "",
      String(fortuneOptions.length || 0),
    ].join("|");

    if (selectedFortuneKeyRef.current === nextKey) {
      return;
    }

    selectedFortuneKeyRef.current = nextKey;
    setSelectedFortune(computedFortune || null);
  }, [computedFortune, fortuneOptions.length, reading]);

  if (!reading) {
    return (
      <section className="mobile-page">
        <Card className="mobile-panel review-card">
          <h1 className="review-title">ผลลัพธ์มูเตลู</h1>
          {loadingLatest ? (
            <p className="review-empty">กำลังโหลดผลลัพธ์จากระบบ...</p>
          ) : (
            <p className="review-empty">
              ยังไม่มีข้อมูลใบเสร็จ กรุณากรอกเลขใบเสร็จก่อน
            </p>
          )}
          <Link to="/game/mutelu/scan" className="review-center-btn">
            <Button className="review-btn btn-uniform">ไปกรอกเลขใบเสร็จ</Button>
          </Link>
        </Card>
      </section>
    );
  }

  const graphCoordinates = toGraphCoordinates(reading.graphPoints);
  const path = buildSmoothPath(graphCoordinates);
  const areaPath = buildAreaPath(graphCoordinates);
  const yAxisTickValues = [100, 75, 50, 25, 0];
  const yAxisDateLabels = buildGraphDateLabels(reading, yAxisTickValues.length);
  const yAxisTicks = yAxisTickValues.map((value, index) => ({
    value,
    label: yAxisDateLabels[index] || "-",
  }));
  const xAxisLabels = buildGraphDateLabels(reading, graphCoordinates.length);
  const currentPoint =
    graphCoordinates[Math.min(3, graphCoordinates.length - 1)] ||
    graphCoordinates[graphCoordinates.length - 1];
  const shopSuggestions = [
    ...(Array.isArray(reading.fixProducts) ? reading.fixProducts.map((item) => ({ item, kind: "fix" })) : []),
    ...(Array.isArray(reading.boostProducts) ? reading.boostProducts.map((item) => ({ item, kind: "boost" })) : []),
  ]
    .map(({ item, kind }) => {
      const normalized = normalizeProductEntry(item);
      if (!normalized) return null;
      return {
        label: normalized.label,
        supportText: normalized.supportText,
        kind,
      };
    })
    .filter(Boolean)
    .filter((item, index, all) => all.findIndex((entry) => entry.label === item.label) === index)
    .map((item) => ({
      ...item,
      kindLabel: item.kind === "fix" ? "สินค้าแก้ดวง" : "สินค้าเสริมดวง",
      supportText:
        item.supportText ||
        getProductSupportText(item.label, reading.dominantElementId),
    }));
  const fortuneHeadline =
    selectedFortune?.headline ||
    reading.fortuneHeadline ||
    "คำทำนายวันนี้";
  const fortuneMessage =
    selectedFortune?.message ||
    reading.fortuneMessage ||
    aiFortuneData?.reviewMessages?.defaultFortuneMessage ||
    "";
  const remedyList = buildRemedyList(reading);
  const elementImage = ELEMENT_IMAGE_MAP[reading?.dominantElementId] || null;
  return (
    <section className="mobile-page">
      <Card className="mobile-panel review-card">
        <div className="review-head">
          <h1 className="review-title">คำทำนายดวงวันนี้</h1>
          <p className="review-receipt">Receipt: {reading.receiptNo}</p>
          {reading.scan?.trn_no ? (
            <p className="review-receipt">TRN: {reading.scan.trn_no}</p>
          ) : null}
          {/* {reading.scan?.scanned_at ? (
            <p className="review-receipt">Scanned: {reading.scan.scanned_at}</p>
          ) : null} */}
        </div>

        <div className="review-element-box">
          <div className="review-element-icon" aria-hidden="true">
            {elementImage ? (
              <img
                src={elementImage}
                alt={reading.dominantElementLabel || "ธาตุประจำวัน"}
                className="review-element-image"
                loading="eager"
              />
            ) : (
              reading.dominantElementSymbol
            )}
          </div>
          <div>
            <p className="review-element-name">
              ทำนายดวงจากตะกร้าช้อปของคุณ
            </p>
            <p className="review-text">
              วันนี้พลังของคุณคือ <strong>{reading.dominantElementLabel}</strong>
            </p>
          </div>
        </div>

        <div className="review-graph-wrap">
          <svg
            viewBox={`0 0 ${GRAPH_LAYOUT.width} ${GRAPH_LAYOUT.height}`}
            className="review-graph"
            role="img"
            aria-label="life graph"
          >
            <defs>
              <linearGradient id="reviewLineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#7c5bbd" />
                <stop offset="60%" stopColor="#5d8ee7" />
                <stop offset="100%" stopColor="#8ecfba" />
              </linearGradient>
              <linearGradient id="reviewAreaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="rgba(116, 157, 229, 0.38)" />
                <stop offset="100%" stopColor="rgba(116, 157, 229, 0.04)" />
              </linearGradient>
            </defs>
            {yAxisTicks.map((tick) => {
              const y = GRAPH_LAYOUT.top + ((100 - tick.value) / 100) * GRAPH_PLOT_HEIGHT;
              return (
                <g key={`y-tick-${tick.value}`}>
                  <line
                    x1={GRAPH_LAYOUT.left}
                    y1={y}
                    x2={GRAPH_LAYOUT.width - GRAPH_LAYOUT.right}
                    y2={y}
                    className={tick.value === 50 ? "review-axis" : "review-axis-soft"}
                  />
                  <text
                    x={GRAPH_LAYOUT.left - 6}
                    y={y + 4}
                    textAnchor="end"
                    className="review-axis-label"
                  >
                    {tick.label}
                  </text>
                </g>
              );
            })}
            <line
              x1={GRAPH_LAYOUT.left}
              y1={GRAPH_LAYOUT.top}
              x2={GRAPH_LAYOUT.left}
              y2={GRAPH_LAYOUT.height - GRAPH_LAYOUT.bottom}
              className="review-axis"
            />
            <line
              x1={GRAPH_LAYOUT.left}
              y1={GRAPH_LAYOUT.height - GRAPH_LAYOUT.bottom}
              x2={GRAPH_LAYOUT.width - GRAPH_LAYOUT.right}
              y2={GRAPH_LAYOUT.height - GRAPH_LAYOUT.bottom}
              className="review-axis"
            />
            <path d={areaPath} className="review-area-fill" />
            <path d={path} className="review-line-glow" />
            <path d={path} className="review-line-main" />
            {graphCoordinates.map((point, index) => (
              <g key={`x-label-${index}`}>
                <line
                  x1={point.x}
                  y1={GRAPH_LAYOUT.height - GRAPH_LAYOUT.bottom}
                  x2={point.x}
                  y2={GRAPH_LAYOUT.height - GRAPH_LAYOUT.bottom + 3}
                  className="review-axis-tick"
                />
                <text
                  x={point.x}
                  y={GRAPH_LAYOUT.height - 10}
                  textAnchor="middle"
                  className="review-axis-label x-axis"
                >
                  {xAxisLabels[index] || "-"}
                </text>
              </g>
            ))}
            {currentPoint ? (
              <>
                <circle cx={currentPoint.x} cy={currentPoint.y} r="14" className="review-dot-float" />
                <circle cx={currentPoint.x} cy={currentPoint.y} r="10" className="review-dot-pulse" />
                <circle cx={currentPoint.x} cy={currentPoint.y} r="5" className="review-dot" />
              </>
            ) : null}
          </svg>
          <p className="review-summary-text review-summary-after-graph">
            สรุปผล จังหวะดวงอยู่ในโหมด <strong>{fortuneHeadline}</strong>
          </p>
          {/* <p className="review-text">
            {fortuneMessage || "ทิศทางดวงชะตาประจำวัน: จุดเรืองแสงที่ปรากฏคือห้วงเวลาปัจจุบัน จังหวะมงคลที่โชคลาภกำลังเคลื่อนเข้าหาคุณ"}
          </p> */}
          
        </div>

        <div className="review-tab-panel">
          <div className={`review-tab-content ${activeTab === 0 ? "is-active" : ""}`}>
            <div className="review-tab-dots" role="tablist" aria-label="ตัวเลือกคำทำนาย">
              {["คำทำนาย", "สินค้าเสริมดวง"].map((label, index) => (
                <button
                  key={label}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === index}
                  aria-label={`ดูแท็บ ${label}`}
                  tabIndex={activeTab === index ? 0 : -1}
                  className={`review-dot-tab ${activeTab === index ? "is-active" : ""}`}
                  onClick={() => setActiveTab(index)}
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="review-tab-body">
              {activeTab === 0 ? (
                <div className="review-guidance review-guidance-highlight">
                  <p className="review-guidance-title">คำทำนาย</p>
                  <p className="review-text review-chatty">{fortuneMessage || "เปิดโหมดดวงปัง จังหวะรับทรัพย์แบบจัดเต็ม!"}</p>
                  <ul className="review-remedy-list">
                    {remedyList.map((tip, index) => renderTipLine(tip, index))}
                  </ul>
                </div>
              ) : (
                <div className="review-shop-box">
                  <p className="review-purchase-title">สินค้าเสริมดวง</p>
                  <p className="review-text">เลือกช้อปตามพลังธาตุของคุณ</p>
                  <div className="review-shop-grid">
                    {shopSuggestions.length > 0 ? (
                      shopSuggestions.map((item) => (
                        <article key={item.label} className="review-shop-item">
                          <p className="review-shop-item-label">{item.label}</p>
                          <p className="review-shop-item-support">{item.supportText}</p>
                          <span className={`review-shop-item-type ${item.kind}`}>{item.kindLabel}</span>
                        </article>
                      ))
                    ) : (
                      <p className="review-text">ยังไม่มีรายการสินค้าเสริมดวง</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          <p className="review-tab-caption">
            {activeTab === 0
              ? "แตะแท็บสินค้าเสริมดวงเพื่อดูรายการแนะนำ"
              : "แตะแท็บคำทำนายเพื่อย้อนกลับผลทำนาย"}
          </p>
        </div>

        <Link to="/game/mutelu/party" className="review-center-btn">
          <Button className="review-btn review-btn-pulse btn-uniform">ไปตั้งตี้คูณทรัพย์</Button>
        </Link>
      </Card>
    </section>
  );
}
